import React, { useMemo, useRef, useEffect, useState } from 'react';

const CodeInput = ({ length = 6, value = '', onChange, disabled = false, shake = false, autoFocus = false }) => {
  const inputsRef = useRef([]);
  const [isShaking, setIsShaking] = useState(false);
  const resolvedLength = Number.isInteger(length) && length > 0 ? length : 6;
  const safeValue = typeof value === 'string' ? value : (value ?? '').toString();
  const chars = useMemo(
    () => Array.from({ length: resolvedLength }, (_, index) => safeValue[index] ?? ''),
    [safeValue, resolvedLength]
  );

  useEffect(() => {
    if (!shake) return;
    setIsShaking(true);
    const timer = setTimeout(() => setIsShaking(false), 500);
    return () => clearTimeout(timer);
  }, [shake]);

  useEffect(() => {
    if (!autoFocus) return;
    focusIndex(0);
  }, [autoFocus]);

  useEffect(() => {
    if (shake) {
      focusIndex(0);
    }
  }, [shake]);

  const focusIndex = (index) => {
    const el = inputsRef.current[index];
    if (el) {
      el.focus();
      el.select();
    }
  };

  const handleChange = (index, nextValue) => {
    const sanitized = nextValue.replace(/\D/g, '');
    if (!sanitized) {
      const updated = chars.slice();
      updated[index] = '';
      onChange(updated.join('').trimEnd());
      return;
    }
    const updated = chars.slice();
    updated[index] = sanitized[0];
    const nextCode = updated.join('');
    onChange(nextCode.trimEnd());
    if (index < resolvedLength - 1) {
      focusIndex(index + 1);
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !chars[index] && index > 0) {
      focusIndex(index - 1);
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, resolvedLength);
    if (!pasted) return;
    const updated = pasted.padEnd(resolvedLength, '').split('');
    onChange(updated.join('').trimEnd());
    const nextIndex = Math.min(pasted.length, resolvedLength - 1);
    focusIndex(nextIndex);
  };

  return (
    <div className={`code-input${isShaking ? ' code-input--shake' : ''}`} onPaste={handlePaste}>
      {chars.map((char, index) => (
        <input
          key={index}
          ref={(el) => { inputsRef.current[index] = el; }}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          className="code-input__box"
          value={char}
          onChange={(event) => handleChange(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          disabled={disabled}
        />
      ))}
    </div>
  );
};

export default CodeInput;
